import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';

const useCountUp = (target, duration = 1500, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const StatItem = ({ stat, animate }) => {
  const count = useCountUp(stat?.numericValue, 1500, animate);
  return (
    <div className="text-center group">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
        style={{ background: stat?.color + '15' }}
      >
        <Icon name={stat?.icon} size={24} color={stat?.color} />
      </div>
      <div className="text-3xl md:text-4xl font-bold text-foreground mb-1">
        {animate ? `${count}${stat?.suffix}` : stat?.display}
      </div>
      <div className="text-text-secondary text-sm">{stat?.label}</div>
    </div>
  );
};

const StatsSection = () => {
  const ref = useRef(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimate(true); },
      { threshold: 0.3 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  const stats = [
    { icon: 'Users', color: '#F58A54', numericValue: 12000, suffix: '+', display: '12,000+', label: 'Stacks Created' },
    { icon: 'Building2', color: '#F06F6B', numericValue: 3500, suffix: '+', display: '3,500+', label: 'Verified Vendors' },
    { icon: 'Star', color: '#E0528A', numericValue: 98, suffix: '%', display: '98%', label: 'Satisfaction Rate' },
    { icon: 'Clock', color: '#F58A54', numericValue: 72, suffix: 'h', display: '72h', label: 'Avg. First Quote' },
  ];

  const testimonials = [
    {
      quote: "Stackd cut our vendor selection time from weeks to days. The comparison tools are genuinely useful.",
      author: "Sarah M.",
      role: "Operations Manager",
      avatar: 'S',
      color: '#F58A54',
    },
    {
      quote: "As a vendor, I get qualified leads with real Stack details. No more chasing vague inquiries.",
      author: "James T.",
      role: "General Contractor",
      avatar: 'J',
      color: '#F06F6B',
    },
    {
      quote: "The milestone tracker kept our renovation on schedule. Highly recommend for any home Stack.",
      author: "Priya K.",
      role: "Homeowner",
      avatar: 'P',
      color: '#E0528A',
    },
  ];

  return (
    <section ref={ref} className="py-20 bg-muted">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Stats */}
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Trusted by Thousands</h2>
          <p className="text-text-secondary text-lg">Real numbers from real Stacks on the Stackd platform</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
          {stats?.map((stat, i) => (
            <StatItem key={i} stat={stat} animate={animate} />
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials?.map((t, i) => (
            <div key={i} className="bg-card border border-border rounded-2xl p-6 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)]?.map((_, si) => (
                  <Icon key={si} name="Star" size={14} color="#F59E0B" />
                ))}
              </div>
              <p className="text-foreground text-sm leading-relaxed mb-5 italic">"{t?.quote}"</p>
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ background: t?.color }}
                >
                  {t?.avatar}
                </div>
                <div>
                  <div className="text-foreground font-semibold text-sm">{t?.author}</div>
                  <div className="text-text-secondary text-xs">{t?.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
